import type Feature from "ol/Feature";
import WKT from "ol/format/WKT";
import MultiPoint from "ol/geom/MultiPoint";
import Polygon from "ol/geom/Polygon";
import Draw from "ol/interaction/Draw";
import Modify from "ol/interaction/Modify";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import CircleStyle from "ol/style/Circle";
import Fill from "ol/style/Fill";
import Stroke from "ol/style/Stroke";
import Style from "ol/style/Style";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  ClearIcon,
  type MapToolState,
  type NgwMapInstance,
  formatArea,
  useMapWorkspace,
} from "../../map-module";
import { CONFIG } from "../config";

import { ReportModal } from "./ReportModal";
import { ReportTaskService } from "./ReportTaskService";
import type { ReportFormValues, WktGeometry } from "./types";

import "./ReportCard.css";

const DEFAULT_AOI: AoiState = {
  valid: false,
  invalid: false,
  text: "Draw an area of interest",
};

const LOCAL_STORAGE_KEY = "active_report_task_id";

export type AoiState = {
  valid: boolean;
  invalid: boolean;
  text: string;
};

export function ReportCard({ activeMode, updateActiveMode }: MapToolState) {
  const [aoiState, setAoiState] = useState<AoiState>(DEFAULT_AOI);
  const canClearAoi = aoiState.valid || aoiState.invalid;

  const { ngwMap } = useMapWorkspace();

  const [reportOpen, setReportOpen] = useState(false);
  const [taskStatus, setTaskStatus] = useState("");
  const [taskId, setTaskId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const isDrawingAoi = activeMode === "draw-aoi";

  const aoiSource = useRef(new VectorSource<Feature<Polygon>>());
  const activeDraw = useRef<Draw | null>(null);
  const vectorSetupDone = useRef(false);

  const taskService = useRef(new ReportTaskService());

  const getServiceCallbacks = useCallback(
    () => ({
      onStatusChange: (statusText: string) => setTaskStatus(statusText),
      onSuccess: () => {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      },
      onFinalize: () => {
        setIsProcessing(false);
        setTaskId(null);
      },
    }),
    [],
  );

  const restoreActiveTask = useCallback(
    async (id: string) => {
      try {
        setTaskStatus("Checking previous task status...");
        setIsProcessing(true);
        setReportSubmitted(true);
        setTaskId(id);
        setReportOpen(true);

        const response = await fetch(`/api/tasks/status/${id}`);
        if (!response.ok) throw new Error("Failed to fetch task status");

        const data = await response.json();

        if (data.status === "processing") {
          setTaskStatus("Restoring task tracking...");
          taskService.current.connect(id, getServiceCallbacks());
        } else {
          localStorage.removeItem(LOCAL_STORAGE_KEY);
          setTaskId(null);
          setIsProcessing(false);
          setTaskStatus(`Previous task ended with status: ${data.status}`);
        }
      } catch (error) {
        console.error("Failed to restore task session:", error);
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        setTaskId(null);
        setIsProcessing(false);
        setTaskStatus("Failed to restore previous task session.");
      }
    },
    [getServiceCallbacks],
  );

  const handleStartReport = async (formValues: ReportFormValues) => {
    const geometry = getAoiWkt();
    if (!aoiState.valid || !geometry) {
      setTaskStatus("Draw a valid area before submitting.");
      return;
    }

    const params = {
      name: formValues.name,
      email: formValues.email,
      organisation: formValues.organisation,
      site_name: formValues.siteName,
      wkt_geom: geometry,
      buffer_meters: CONFIG.bufferMeters,
    };

    try {
      setTaskStatus("Sending request...");
      setIsProcessing(true);
      setReportSubmitted(true);

      const response = await fetch("/api/tasks/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });

      if (!response.ok) throw new Error("Server rejected task initialization");

      const data = await response.json();
      if (data.task_id) {
        localStorage.setItem(LOCAL_STORAGE_KEY, data.task_id);
        setTaskId(data.task_id);
        taskService.current.connect(data.task_id, getServiceCallbacks());
      } else {
        throw new Error("Missing task_id in server response");
      }
    } catch (error) {
      setTaskStatus(
        `Error: ${error && typeof error === "object" && "message" in error ? error.message : "Failed to submit request"}`,
      );
      setIsProcessing(false);
    }
  };

  const handleCancelReport = async () => {
    if (!taskId) return;

    try {
      setTaskStatus("Aborting task...");
      taskService.current.disconnect();

      const response = await fetch(`/api/tasks/cancel/${taskId}`, {
        method: "POST",
      });

      if (response.ok) {
        setTaskStatus("Task cancelled successfully.");
      } else {
        setTaskStatus("Failed to abort the task on the server.");
      }
    } catch {
      setTaskStatus("Network error occurred during cancellation.");
    } finally {
      setIsProcessing(false);
      setTaskId(null);
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
  };

  useEffect(() => {
    const ts = taskService.current;
    const savedTaskId = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (savedTaskId) {
      restoreActiveTask(savedTaskId);
    }

    return () => {
      ts.disconnect();
    };
  }, [restoreActiveTask]);

  const validateAoi = useCallback(() => {
    const maxExtentArea = CONFIG.maxExtentArea;
    const feature = aoiSource.current.getFeatures()[0] as
      | Feature<Polygon>
      | undefined;

    if (!feature) {
      setAoiState(DEFAULT_AOI);
      return;
    }

    const geometry = feature.getGeometry();
    if (!geometry) {
      setAoiState(DEFAULT_AOI);
      return;
    }

    const extent = geometry.getExtent();
    const extentArea =
      Math.max(0, extent[2] - extent[0]) * Math.max(0, extent[3] - extent[1]);
    const hasMaxExtentArea =
      typeof maxExtentArea === "number" && maxExtentArea > 0;
    const valid =
      extentArea > 0 && (!hasMaxExtentArea || extentArea <= maxExtentArea);
    const invalid = !valid;
    const maxAreaText = hasMaxExtentArea ? formatArea(maxExtentArea) : "";

    if (feature.get("invalid") !== invalid) {
      feature.set("invalid", invalid, true);
      feature.changed();
    }

    setAoiState({
      valid,
      invalid,
      text: valid
        ? `Area extent ${formatArea(extentArea)}`
        : extentArea > 0 && hasMaxExtentArea
          ? `Area is too large: ${formatArea(extentArea)}. Maximum: ${maxAreaText}. Resize the polygon or draw a new one.`
          : "Draw a non-zero area.",
    });
  }, []);

  const setupVectorLayers = useCallback(
    (map: NgwMapInstance) => {
      const olMap = map.mapAdapter?.map;
      if (!olMap || vectorSetupDone.current) return;
      vectorSetupDone.current = true;

      const aoiLayer = new VectorLayer({
        source: aoiSource.current,
        style: (feature) => {
          const invalid = feature.get("invalid") === true;
          const strokeColor = invalid ? "#b02a37" : "#5d8f13";
          const fillColor = invalid
            ? "rgba(176, 42, 55, 0.12)"
            : "rgba(147, 193, 61, 0.18)";

          return [
            new Style({
              fill: new Fill({
                color: fillColor,
              }),
              stroke: new Stroke({
                color: strokeColor,
                width: 3,
              }),
            }),
            new Style({
              geometry: (styledFeature) => {
                const geometry = styledFeature.getGeometry();
                if (geometry instanceof Polygon) {
                  const ring = geometry.getCoordinates()[0] ?? [];
                  return new MultiPoint(ring.slice(0, -1));
                }
              },
              image: new CircleStyle({
                radius: 5,
                fill: new Fill({ color: strokeColor }),
                stroke: new Stroke({
                  color: strokeColor,
                  width: 2,
                }),
              }),
            }),
          ];
        },
        zIndex: 1000,
      });

      olMap.addLayer(aoiLayer);

      const modify = new Modify({ source: aoiSource.current });
      modify.on("modifyend", validateAoi);
      olMap.addInteraction(modify);

      aoiSource.current.on("addfeature", validateAoi);
      aoiSource.current.on("removefeature", validateAoi);
      validateAoi();
    },
    [validateAoi],
  );

  const clearAoi = useCallback(() => {
    aoiSource.current.clear();
  }, []);

  const getAoiWkt = useCallback(() => {
    const feature = aoiSource.current.getFeatures()[0];
    if (!feature) return null;

    return new WKT().writeFeature(feature, {
      dataProjection: "EPSG:4326",
      featureProjection: "EPSG:3857",
    }) as WktGeometry | null;
  }, []);

  useEffect(() => {
    if (ngwMap) {
      setupVectorLayers(ngwMap);
    }
  }, [ngwMap, setupVectorLayers]);

  useEffect(() => {
    if (!ngwMap) return;
    const olMap = ngwMap.mapAdapter?.map;
    if (!olMap) return;

    if (activeDraw.current) {
      olMap.removeInteraction(activeDraw.current);
      activeDraw.current = null;
    }

    if (!activeMode) return;

    if (activeMode === "draw-aoi") {
      const draw = new Draw({
        source: aoiSource.current,
        type: "Polygon",
      });

      draw.on("drawstart", () => {
        aoiSource.current.clear();
      });
      draw.on("drawend", () => {
        window.setTimeout(() => {
          validateAoi();
          updateActiveMode("draw-aoi", activeMode !== "draw-aoi");
        });
      });

      activeDraw.current = draw;
      olMap.addInteraction(draw);
    }

    const draw = activeDraw.current;
    return () => {
      if (draw) {
        olMap.removeInteraction(draw);
        if (activeDraw.current === draw) {
          activeDraw.current = null;
        }
      }
    };
  }, [activeMode, ngwMap, updateActiveMode, validateAoi]);

  const handleOpenModal = () => {
    if (!isProcessing) {
      setTaskStatus("");
      setReportSubmitted(false);
    }
    setReportOpen(true);
  };

  const handlePrimaryAction = () => {
    if (isProcessing || aoiState.valid) {
      handleOpenModal();
      return;
    }

    updateActiveMode("draw-aoi", !isDrawingAoi);
  };

  const primaryActionLabel = isProcessing
    ? "View progress"
    : aoiState.valid
      ? "Generate report"
      : isDrawingAoi
        ? "Cancel drawing"
        : "Draw site boundary";

  const stepHint = isProcessing ? (
    "Report generation is running."
  ) : aoiState.valid ? (
    "Boundary is ready. Generate the report and enter contact details."
  ) : aoiState.invalid ? (
    aoiState.text
  ) : isDrawingAoi ? (
    <>
      <span>Click the map to add boundary points.</span>
      <br />
      <span>Double-click the final point to finish.</span>
    </>
  ) : (
    "Start by drawing the site boundary on the map."
  );

  return (
    <>
      <div className="report-card">
        <div className="report-steps" aria-label="Report steps">
          <div
            className={
              aoiState.valid
                ? "report-step is-complete"
                : "report-step is-active"
            }
          >
            <span className="report-step-index">1</span>
            <span>Draw boundary</span>
          </div>
          <div
            className={
              aoiState.valid || isProcessing
                ? "report-step is-active"
                : "report-step"
            }
          >
            <span className="report-step-index">2</span>
            <span>Generate report</span>
          </div>
        </div>
        <div className="report-actions">
          {canClearAoi && (
            <button
              className="icon-button report-tool-button"
              type="button"
              title="Delete area of interest"
              onClick={clearAoi}
            >
              <ClearIcon className="tool-svg tool-svg-danger" />
            </button>
          )}
          <button
            className="primary-button"
            type="button"
            aria-pressed={isDrawingAoi}
            onClick={handlePrimaryAction}
          >
            {primaryActionLabel}
          </button>
        </div>
        <div
          className={
            aoiState.invalid ? "aoi-state aoi-state-invalid" : "aoi-state"
          }
          role="status"
        >
          <span
            className={
              aoiState.valid
                ? "state-dot state-valid"
                : aoiState.invalid
                  ? "state-dot state-invalid"
                  : "state-dot"
            }
            aria-hidden="true"
          />
          <span>{stepHint}</span>
        </div>
      </div>

      <ReportModal
        open={reportOpen}
        status={taskStatus}
        isProcessing={isProcessing}
        showForm={!reportSubmitted && !isProcessing}
        onCancel={handleCancelReport}
        onClose={() => setReportOpen(false)}
        onSubmit={handleStartReport}
        termsUrl={CONFIG.termsAndConditionsUrl}
      />
    </>
  );
}
